/**
 * SingleSelectCombobox Component
 *
 * A searchable single-select combobox, built on the same shadcn/Base UI
 * Combobox primitive as MultiSelectCombobox so single- and multi-select fields
 * share one consistent look, behaviour, and keyboard support. Kept in
 * components/shared for reuse across forms (survey, dataset, etc.).
 *
 * Each option has a stable `value` (stored/sent) and a display `label`.
 * `itemToStringValue` ensures filtering and the input text operate on the
 * label, not the value.
 */

import { useCallback, useLayoutEffect, useMemo, useRef, useState, type JSX } from "react"
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox"

/** A single selectable option. */
export interface ComboboxOption {
  /** The stable value stored in state and sent to the API. */
  value: string
  /** The human-readable label shown to the user. */
  label: string
}

/** Props for the SingleSelectCombobox. */
interface SingleSelectComboboxProps {
  /** The available options to choose from. */
  options: ComboboxOption[]
  /** The currently selected value ("" when nothing is selected). */
  value: string
  /** Called with the next selected value ("" when cleared). */
  onChange: (next: string) => void
  /** Placeholder shown in the input when nothing is selected. */
  placeholder?: string
  /** Message shown when the search matches no options. */
  emptyMessage?: string
  /** Optional id used to associate an external <label>. */
  id?: string
  /** When true, the combobox is non-interactive and visually muted. */
  disabled?: boolean
  /**
   * Whether to show the inline clear (X) button that resets the selection to
   * "". Defaults to true. Set to false when a selection is mandatory and the
   * user should not be able to clear back to an empty value.
   */
  showClear?: boolean
  /**
   * When true, the user may commit a value they typed that is not among
   * `options`: the current input text is offered as an extra, selectable option
   * and `onChange` fires with that raw string. Used for "pick an existing one
   * or type a new one" fields (e.g. the diagram folder). Defaults to false, in
   * which case only the provided `options` can be selected.
   */
  allowCustomValue?: boolean
  /**
   * Optional element (or ref) to portal the dropdown into, as in
   * MultiSelectCombobox. A modal Radix Dialog or Sheet blocks pointer events
   * outside its own DOM subtree, so the default body-level dropdown would show
   * but not respond to clicks. When omitted, the combobox portals into the
   * closest enclosing `role="dialog"` element itself, so dialogs and drawers
   * work without passing anything.
   */
  portalContainer?:
    | HTMLElement
    | null
    | React.RefObject<HTMLElement | null>
  className?: string
}

/**
 * A searchable single-select combobox.
 *
 * @param options - The available options.
 * @param value - The currently selected value ("" when none).
 * @param onChange - Callback invoked with the selected value ("" when cleared).
 * @param placeholder - Input placeholder when nothing is selected.
 * @param emptyMessage - Message shown when no options match the search.
 * @param id - Optional id used to associate an external <label>.
 * @param disabled - Whether the combobox is disabled.
 * @param showClear - Whether to show the inline clear (X) button (default true).
 * @param allowCustomValue - Whether a typed, not-in-list value can be selected (default false).
 * @param portalContainer - Element or ref to portal the dropdown into (default: the enclosing dialog, if any).
 * @param className - Optional extra classes applied to the input group.
 * @returns The rendered single-select combobox.
 */
export default function SingleSelectCombobox({
  options,
  value,
  onChange,
  placeholder = "Select…",
  emptyMessage = "No items found.",
  id,
  disabled = false,
  showClear = true,
  allowCustomValue = false,
  portalContainer,
  className,
}: SingleSelectComboboxProps): JSX.Element {
  // The current input text, tracked only in custom-value mode so a typed value
  // that matches no option can still be offered and committed. Unused (and the
  // extra option is never added) when allowCustomValue is false.
  const [inputText, setInputText] = useState<string>("")

  // In custom-value mode, offer the trimmed input text as an extra option when
  // it is non-empty and not already an option, so the user can select what they
  // typed. Otherwise the options are exactly those passed in.
  const effectiveOptions = useMemo<ComboboxOption[]>(() => {
    if (!allowCustomValue) {
      return options
    }
    const typed: string = inputText.trim()
    if (typed === "" || options.some(option => option.value === typed)) {
      return options
    }
    return [...options, { value: typed, label: typed }]
  }, [allowCustomValue, options, inputText])

  // Resolve the selected value string back to its full option object, which is
  // what the Combobox works with. In custom-value mode a committed value may
  // not be in `options`, so synthesise an option for it. Null means nothing is
  // selected.
  const selectedOption = useMemo<ComboboxOption | null>(() => {
    const existing: ComboboxOption | undefined = effectiveOptions.find(
      option => option.value === value
    )
    if (existing !== undefined) {
      return existing
    }
    if (allowCustomValue && value !== "") {
      return { value, label: value }
    }
    return null
  }, [effectiveOptions, value, allowCustomValue])

  // The dropdown is portalled (to <body> by default). Inside a modal Radix
  // dialog or sheet, everything outside the dialog has pointer events disabled,
  // so a dropdown portalled to <body> shows but cannot be clicked. Unless the
  // caller passes `portalContainer`, find an enclosing dialog and portal there.
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const [enclosingDialog, setEnclosingDialog] = useState<HTMLElement | null>(null)
  useLayoutEffect(() => {
    const dialog: Element | null | undefined = anchorRef.current?.closest('[role="dialog"]')
    setEnclosingDialog(dialog instanceof HTMLElement ? dialog : null)
  }, [])

  // When the selection changes, hand the parent just the value string ("" when
  // the selection is cleared) so call sites keep a simple string API.
  const handleValueChange = useCallback(
    (next: ComboboxOption | null) => {
      onChange(next?.value ?? "")
    },
    [onChange]
  )

  return (
    // `contents` keeps this wrapper out of layout; it only lets the effect above
    // find an enclosing dialog.
    <div ref={anchorRef} className="contents">
    <Combobox
      items={effectiveOptions}
      autoHighlight
      value={selectedOption}
      onValueChange={handleValueChange}
      onInputValueChange={allowCustomValue ? setInputText : undefined}
      itemToStringValue={(option: ComboboxOption) => option.label}
      disabled={disabled}
    >
      <ComboboxInput
        id={id}
        placeholder={placeholder}
        className={className ?? "w-full"}
        showClear={showClear}
      />
      <ComboboxContent container={portalContainer ?? enclosingDialog ?? undefined}>
        <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
        <ComboboxList>
          {(option: ComboboxOption) => (
            <ComboboxItem key={option.value} value={option}>
              {option.label}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
    </div>
  )
}
